import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { Typography } from "@mui/material"

import { MetricGoalContainer, NoGroupWrapper } from "./Atom"
import { MetricGoalsTable } from "../../components/Table"

import MetricAPI from "../../../api/metric"

import {
  clearLocalstorage,
  getCurrentYear,
  getTokenLocal,
} from "../../../helper/utils"

import {
  setMaevaGroups,
  setMaevaPractices,
  setMaevaRegions,
  setMaevaSelectedPractices,
  setMetricGoalsTabs,
  setMetricGoalsYearList,
  setSelectedMetricGoalsTab,
} from "../../redux/slices/metricSlice"

import { setLoading, setOpenSidebar } from "../../redux/slices/appSlice"
import MetricGoalsTabs from "../../components/Tabs/MetricGoalsTabs"
import MetricGoalsHeader from "../../components/Headers/MetricGoalsHeader"
import { useResponseHandle } from "../../hooks"
import { useNavigate } from "react-router-dom"
import ResourceBudgetAPI from "../../../api/resourceBudget"

const metricAPI = new MetricAPI()
const rbAPI = new ResourceBudgetAPI()

export default function MetricGoal() {
  const navigate = useNavigate()
  const token: any = getTokenLocal()
  const dispatch = useDispatch()

  dispatch(setOpenSidebar(false))

  const currentYear = getCurrentYear()

  const maevaSelectedGroup = useSelector((state: any) => {
    return state.metric.maevaSelectedGroupsNoAll?.[0]
  })
  const maevaSelectedRegion = useSelector((state: any) => {
    return state.metric.maevaSelectedRegions?.[0]
  })
  const maevaSelectedPractice = useSelector((state: any) => {
    return state.metric.maevaSelectedPractices?.[0]
  })

  const selectedDateFilter = useSelector((state: any) => {
    return state.metric.metricGoalsSelectedYear
  })

  const selectedMetricGoalsTab = useSelector((state: any) => {
    return state.metric.selectedMetricGoalsTab
  })

  const fetchAssignedBucketsToGroup = async () => {
    dispatch(setLoading(true))

    if (token) {
      const response: any = await metricAPI.getAssignedBucketsToGroup(
        token,
        maevaSelectedGroup.group_sk,
      )
      if (
        response?.data?.status_code != 200 &&
        response?.data?.status_code != 401
      ) {
        toast.error(response?.data?.display_message)
      } else {
        dispatch(setMetricGoalsTabs(response?.data?.data))
        dispatch(setLoading(false))
        return response.data?.data
      }
    }

    dispatch(setLoading(false))
  }

  const fetchGroupList = async () => {
    dispatch(setLoading(true))
    const response = await metricAPI.getMetricGroupsBoarded(token)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const groups: Array<Object> = []
        result?.data?.forEach((group: any) => {
          groups.push({ ...group, label: group.group_name })
        })
        dispatch(setMaevaGroups(groups))
        // dispatch(setMaevaSelectedRegions([{ label: "All", region: "all" }]))
        // dispatch(setMaevaSelectedPractices([{ label: "All", name: "all" }]))
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }

    dispatch(setLoading(false))
    return result
  }

  const fetchRegionList = async () => {
    dispatch(setLoading(true))

    const response = await metricAPI.getMetricRegionsByGroups(token, [
      maevaSelectedGroup.group_sk,
    ])

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const regions: Array<Object> = [{ label: "All", region: "all" }]
        result?.data?.regions?.forEach((region: any) => {
          regions.push({ label: region, region: region.toLowerCase() })
        })

        dispatch(setMaevaRegions(regions))
        // dispatch(setMaevaSelectedRegions([regions[0]]))
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }

    dispatch(setLoading(false))
    return result
  }

  const fetchPracticeList = async () => {
    dispatch(setLoading(true))
    const response = await metricAPI.getMetricPracticesByRegions(
      token,
      [maevaSelectedGroup.group_sk],
      [maevaSelectedRegion.region],
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const practices: Array<Object> = [{ label: "All", practice_sk: "all" }]
        result?.data?.forEach((practice: any) => {
          practices.push({ ...practice, label: practice?.name })
        })
        dispatch(setMaevaPractices(practices))
        // dispatch(setMaevaSelectedPractices([practices[0]]))
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }

    dispatch(setLoading(false))
    return result
  }
  const fetchAvailableYearsList = async () => {
    dispatch(setLoading(true))
    const response = await metricAPI.getAllAvailableYears(
      token,
      maevaSelectedGroup?.group_sk,
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const years: Array<Object> = []
        result?.data?.forEach((year: any) => {
          years.push({ label: year.year })
        })
        // Sort years in descending order (most recent first)
        years.sort((a: any, b: any) => {
          const yearA = parseInt(a.label)
          const yearB = parseInt(b.label)
          return yearB - yearA
        })
        dispatch(setMetricGoalsYearList(years))
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }

    dispatch(setLoading(false))
    return result
  }

  const updateYearGoal = async (data: any, showLoading: boolean = true) => {
    if (showLoading) {
      dispatch(setLoading(true))
    }

    const response = await metricAPI.setYearGoals(token, data)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
    })

    if (result?.isAuthorized) {
    } else {
      // clearLocalstorage()
      // navigate("/auth")
    }

    if (showLoading) {
      dispatch(setLoading(false))
    }

    return result
  }

  const updateMonthGoal = async (data: any, showLoading: boolean = true) => {
    if (showLoading) {
      dispatch(setLoading(true))
    }

    const response = await metricAPI.setMonthGoals(token, data)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
    })

    if (result?.isAuthorized) {
    } else {
      // clearLocalstorage()
      // navigate("/auth")
    }

    if (showLoading) {
      dispatch(setLoading(false))
    }

    return result
  }

  const fetchGoalsByBucket = async (bucketId: any, newValue?: any) => {
    if (bucketId) {
      dispatch(setLoading(true))

      const response = await metricAPI.getGoalsByBucket(
        token,
        maevaSelectedGroup?.group_sk,
        bucketId,
        selectedDateFilter?.label || currentYear,
      )

      const result = useResponseHandle({
        apiResponse: response,
        successMessage: false,
      })

      if (result?.isAuthorized) {
        if (result?.statusCode == 200) {
          dispatch(
            setSelectedMetricGoalsTab({
              ...response?.data?.data,
              index: newValue,
            }),
          )
        }
      } else {
        clearLocalstorage()
        navigate("/auth")
      }

      dispatch(setLoading(false))

      return result
    }
  }

  useEffect(() => {
    fetchGroupList()
  }, [])

  useEffect(() => {
    fetchRegionList()
    fetchAvailableYearsList()
  }, [maevaSelectedGroup])

  useEffect(() => {
    fetchPracticeList()
  }, [maevaSelectedRegion])

  useEffect(() => {
    fetchAssignedBucketsToGroup()
  }, [maevaSelectedGroup, selectedDateFilter])

  useEffect(() => {
    fetchGoalsByBucket(
      selectedMetricGoalsTab?.metric_bucket_id,
      selectedMetricGoalsTab?.index,
    )
  }, [selectedDateFilter])

  return (
    <MetricGoalContainer>
      <MetricGoalsHeader name="Metrics Goals" />
      {maevaSelectedGroup ? (
        <>
          <MetricGoalsTabs fetchGoalsByBucket={fetchGoalsByBucket} />
          <MetricGoalsTable
            updateMonthGoal={updateMonthGoal}
            updateYearGoal={updateYearGoal}
            fetchGoalsByBucket={fetchGoalsByBucket}
          />
        </>
      ) : (
        <NoGroupWrapper>
          <Typography>Select A Group</Typography>
        </NoGroupWrapper>
      )}
    </MetricGoalContainer>
  )
}
